/* eslint-disable no-param-reassign */
import { decode as decodeJwt } from '@/common/helpers/jwt/jwt';
import { sentryDSN, env } from '@/common/config';
import { parseLocalStorage } from '@/common/hooks/useGlobalState';

const sensitiveKeys = ['username', 'firstName', 'lastName', 'phoneNumber', 'email', 'emailAddress', 'address', 'city', 'zipcode'];

// RegExp from validate.js, edited to match substrings
// eslint-disable-next-line max-len, no-useless-escape, no-control-regex
const emailRegex = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;

export const scrubPersonalData = (data) => {
  // Mask emails by format
  if (typeof data === 'string' && emailRegex.test(data)) {
    return '*Masked(email)*';
  }

  if (Array.isArray(data)) {
    return data.map(scrubPersonalData);
  }

  if (typeof data === 'object' && data) {
    // Mask object contents by key
    Object.keys(data).forEach((key) => {
      // Ignore casing and non-alphanumeric characters in object keys
      const plain = (_key) => _key.toLowerCase().replace(/[\W_]+/gu, '');
      const isSensitive = sensitiveKeys.some((sensitive) => plain(key) === plain(sensitive));
      if (isSensitive) {
        data[key] = `*Masked(${key})*`;
      } else {
        data[key] = scrubPersonalData(data[key]);
      }
    });
  }

  return data;
};

// Includes non-handleable exceptions and network errors such
// as when a Promise is cancelled after going to /token.
const ignoreErrors = [
  'observablequery with this id doesn\'t exist',
  'cancelled',
  'the network connection was lost.',
  'the internet connection appears to be offline.',
  'networkerror when attempting to fetch resource.',
  'failed to fetch',
  'load failed',
];

export const shouldIgnore = (error) => {
  const message = error?.message?.replace(/^(.*):\s(.*)/u, '$2');
  return ignoreErrors.includes(message?.toLowerCase());
};

export const initSentry = (Sentry) => {
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: sentryDSN,
      environment: env,
      // We want the Sentry event to have user data, so we can scrub any
      // sensitive info to be sent (and keep the user ID).
      // https://docs.sentry.io/platforms/javascript/data-management/sensitive-data/#personally-identifiable-information-pii
      sendDefaultPii: true,
      beforeSend(event, { originalException: error }) {
        return shouldIgnore(error) ? null : scrubPersonalData(event);
      },
    });

    const { userToken, impersonateId } = parseLocalStorage();

    if (userToken) {
      const { oid } = decodeJwt(userToken);
      Sentry.setUser({ id: oid, impersonateId });
    }

    return true;
  }

  return false;
};
